import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Divider,
    Stack,
    Chip,
    Alert,
    CircularProgress,
} from "@mui/material";
import { Close, Add, Delete, Refresh, Save } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import useGetProgramPdfSections from "../../../hooks/programs/useGetProgramPdfSections";
import useSaveProgramPdfTexts from "../../../hooks/programs/useSaveProgramPdfTexts";
import useResetProgramPdfTexts from "../../../hooks/programs/useResetProgramPdfTexts";

const PdfSectionsModal = ({ open, onClose, programId }) => {
    const { i18n } = useTranslation();
    const lang = i18n.language;

    // Hooks
    const { pdfSections, isLoading, refetch } = useGetProgramPdfSections(programId);
    const { savePdfTextsMutate, isPending: isSaving } = useSaveProgramPdfTexts();
    const { resetPdfTextsMutate, isPending: isResetting } = useResetProgramPdfTexts();

    // Local state for form data
    const [formData, setFormData] = useState({});

    // Initialize form data when pdfSections loads
    useEffect(() => {
        if (pdfSections) {
            setFormData(pdfSections);
        }
    }, [pdfSections]);

    // Handle title change
    const handleTitleChange = (section, value) => {
        setFormData((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                title: value,
            },
        }));
    };

    // Handle note change
    const handleNoteChange = (section, value) => {
        setFormData((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                note: value,
            },
        }));
    };

    // Handle item change
    const handleItemChange = (section, index, value) => {
        setFormData((prev) => {
            const newItems = [...(prev[section]?.items || [])];
            newItems[index] = value;
            return {
                ...prev,
                [section]: {
                    ...prev[section],
                    items: newItems,
                },
            };
        });
    };

    // Add new item
    const handleAddItem = (section) => {
        setFormData((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                items: [...(prev[section]?.items || []), ""],
            },
        }));
    };

    // Remove item
    const handleRemoveItem = (section, index) => {
        setFormData((prev) => {
            const newItems = [...(prev[section]?.items || [])];
            newItems.splice(index, 1);
            return {
                ...prev,
                [section]: {
                    ...prev[section],
                    items: newItems,
                },
            };
        });
    };

    // Save changes
    const handleSave = () => {
        const dataToSend = {
            pdf_section_overrides: {},
        };

        // Convert formData to the required format with ar/en
        Object.keys(formData).forEach((sectionKey) => {
            dataToSend.pdf_section_overrides[sectionKey] = {
                title: {
                    [lang]: formData[sectionKey].title,
                },
                items: {
                    [lang]: formData[sectionKey].items,
                },
            };

            if (formData[sectionKey].note) {
                dataToSend.pdf_section_overrides[sectionKey].note = {
                    [lang]: formData[sectionKey].note,
                };
            }
        });

        savePdfTextsMutate(
            { id: programId, data: dataToSend },
            {
                onSuccess: () => {
                    refetch();
                    onClose();
                },
            }
        );
    };

    // Reset to default
    const handleReset = () => {
        resetPdfTextsMutate(programId, {
            onSuccess: () => {
                refetch();
            },
        });
    };

    // Section titles mapping
    const sectionTitles = {
        inclusion_policy: lang === 'ar' ? "سياسة الشمول والإلغاء" : "Inclusion & Cancellation Policy",
        // program_price: lang === 'ar' ? "سعر البرنامج" : "Program Price",
        exclusions: lang === 'ar' ? "الاستثناءات" : "Exclusions",
        important_notes: lang === 'ar' ? "ملاحظات هامة" : "Important Notes",
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: { minHeight: "80vh" }
            }}
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="bold">
                        {lang === 'ar' ? "تخصيص أقسام PDF" : "Customize PDF Sections"}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Stack spacing={3}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {lang === 'ar' 
                                ? "يمكنك تخصيص النصوص التي ستظهر في ملف PDF. اتركها فارغة لاستخدام القيم الافتراضية."
                                : "Customize the texts that will appear in the PDF file. Leave empty to use default values."}
                        </Alert>

                        {Object.keys(formData).map((sectionKey) => (
                            <Box key={sectionKey}>
                                <Chip 
                                    label={sectionTitles[sectionKey]} 
                                    color="primary" 
                                    sx={{ mb: 2, fontWeight: "bold" }}
                                />

                                {/* Section Title */}
                                <TextField
                                    fullWidth
                                    label={lang === 'ar' ? "عنوان القسم" : "Section Title"}
                                    value={formData[sectionKey]?.title || ""}
                                    onChange={(e) => handleTitleChange(sectionKey, e.target.value)}
                                    size="small"
                                    sx={{ mb: 2 }}
                                />

                                {/* Items */}
                                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                    {lang === 'ar' ? "العناصر" : "Items"}
                                </Typography>
                                <Stack spacing={1} mb={2}>
                                    {formData[sectionKey]?.items?.map((item, index) => (
                                        <Stack key={index} direction="row" spacing={1}>
                                            <TextField
                                                fullWidth
                                                value={item}
                                                onChange={(e) => handleItemChange(sectionKey, index, e.target.value)}
                                                size="small"
                                                placeholder={`${lang === 'ar' ? 'عنصر' : 'Item'} ${index + 1}`}
                                            />
                                            <IconButton
                                                color="error"
                                                size="small"
                                                onClick={() => handleRemoveItem(sectionKey, index)}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Stack>
                                    ))}
                                </Stack>

                                <Button
                                    size="small"
                                    startIcon={<Add />}
                                    onClick={() => handleAddItem(sectionKey)}
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                >
                                    {lang === 'ar' ? "إضافة عنصر" : "Add Item"}
                                </Button>

                                {/* Note */}
                                <TextField
                                    fullWidth
                                    label={lang === 'ar' ? "ملاحظة (اختياري)" : "Note (Optional)"}
                                    value={formData[sectionKey]?.note || ""}
                                    onChange={(e) => handleNoteChange(sectionKey, e.target.value)}
                                    size="small"
                                    multiline
                                    rows={2}
                                />

                                <Divider sx={{ mt: 2 }} />
                            </Box>
                        ))}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<Refresh />}
                    onClick={handleReset}
                    disabled={isResetting || isLoading}
                >
                    {isResetting ? (
                        <CircularProgress size={20} />
                    ) : (
                        lang === 'ar' ? "استعادة الافتراضي" : "Reset to Default"
                    )}
                </Button>
                
                <Box flex={1} />

                <Button onClick={onClose} disabled={isSaving || isResetting}>
                    {lang === 'ar' ? "إلغاء" : "Cancel"}
                </Button>
                
                <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSave}
                    disabled={isSaving || isLoading}
                >
                    {isSaving ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (
                        lang === 'ar' ? "حفظ التغييرات" : "Save Changes"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PdfSectionsModal;
